// src/components/form/VendorRegistration.jsx

import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import * as Alert from "../common/Alert";
import * as UserServices from "../../Services/UserServices";
import { CheckMobileNumber, CheckEmail } from "../../Services/MainServices";
import Modal from "../other/Modal";
import { fetchContent } from "../../redux/actions/contentActions";
import loginBanner from "../../assets/banner/loginBanner.png";
import { useDispatch, useSelector } from "react-redux";
// ^^^ UPDATE: import from “vendorRegistrationActions.js” (not vendorRegistrationAction)
import {
  setVendorDraft,
  clearVendorDraft,
} from "../../redux/actions/vendorRegistrationActions";
import { Link, useNavigate } from "react-router-dom";

const VendorRegistration = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // 1) Grab draft from Redux
  const draft = useSelector((state) => state.vendorRegistration.draft);

  // Local UI state
  const [loading, setLoading] = useState(false);
  const [mobileStatus, setMobileStatus] = useState("");
  const [mobileMsg, setMobileMsg] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const {
    loading: tLoading,
    data: tData,
    error: tError,
  } = useSelector((s) => s.content);

  // Add these new state variables for email validation
  const [emailStatus, setEmailStatus] = useState("");
  const [emailMsg, setEmailMsg] = useState("");

  // 2) Formik using Redux draft as initialValues
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      vendor_name: draft.vendor_name || "",
      mobile_no: draft.mobile_no || "",
      email_id: draft.email_id || "",
      vendor_shop_name: draft.vendor_shop_name || "",
      gst_number: draft.gst_number || "",
      address: draft.address || "",
      pin_code: draft.pin_code || "",
    },
    validationSchema: Yup.object({
      vendor_name: Yup.string().required("Full name is required"),
      mobile_no: Yup.string()
        .matches(/^\d{10}$/, "Mobile number must be 10 digits")
        .required("Mobile number is required"),
      email_id: Yup.string()
        .email("Invalid email")
        .required("Email is required"),
      vendor_shop_name: Yup.string().required("Shop name is required"),
      gst_number: Yup.string().matches(
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9]{1}[A-Z]{1}[0-9]{1}$/,
        "Invalid GST number format"
      ),
      address: Yup.string(),
      pin_code: Yup.string()
        .matches(/^\d{6}$/, "Pin code must be 6 digits")
        .required("Pin code is required"),
    }),
    onSubmit: async (values) => {
      try {
        // Check if mobile or email is already registered
        if (mobileStatus === "exists" || emailStatus === "exists") {
          const message =
            mobileStatus === "exists"
              ? "This mobile number is already registered."
              : "This email is already registered.";
          Alert.Alert.ErrorAlert(message);
          return;
        }

        setLoading(true);

        // Trim whitespace
        const cleanedValues = Object.fromEntries(
          Object.entries(values).map(([k, v]) => [
            k,
            typeof v === "string" ? v.trim() : v,
          ])
        );

        const response = await UserServices.vendorRegistration(cleanedValues);
        if (response.status === 200 || response.status === 201) {
          const vendorId = response.data.data.vendor_id;
          if (vendorId) {
            localStorage.setItem("vendor_id", vendorId);
            Alert.Alert.SuccessAlert("Registration Successful !!!");

            // Navigate to KYC, passing mobile_no and vendor_id via router state
            navigate("/vendor-kyc", {
              state: {
                mobile_no: cleanedValues.mobile_no,
                vendor_id: vendorId,
              },
            });
          }
        } else {
          const data = response.data;
          if (data.errors && typeof data.errors === "object") {
            const messages = Object.values(data.errors).flat().join("\n");
            Alert.Alert.ErrorAlert(messages);
          } else if (data.message) {
            Alert.Alert.ErrorAlert(data.message);
          } else {
            Alert.Alert.ErrorAlert("Network error. Please try again.");
          }
        }
      } catch (err) {
        const data = err.response?.data;
        if (data?.errors && typeof data.errors === "object") {
          const messages = Object.values(data.errors).flat().join("\n");
          Alert.Alert.ErrorAlert(messages);
        } else if (data?.message) {
          Alert.Alert.ErrorAlert(data.message);
        } else {
          Alert.Alert.ErrorAlert("Network error. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    },
  });

  // 3) Sync Formik values → Redux draft on every change
  useEffect(() => {
    dispatch(setVendorDraft(formik.values));
  }, [formik.values, dispatch]);

  // 4) Dynamic mobile‐number check (unchanged)
  useEffect(() => {
    const mobile = formik.values.mobile_no;
    if (mobile.length === 10) {
      const timer = setTimeout(() => {
        CheckMobileNumber(mobile)
          .then((res) => {
            if (res.data.exists) {
              setMobileStatus("exists");
              setMobileMsg("This number already exists.");
            } else {
              setMobileStatus("available");
              setMobileMsg("Looks good!");
            }
          })
          .catch(() => {
            setMobileStatus("");
            setMobileMsg("Error checking number.");
          });
      }, 400);
      return () => clearTimeout(timer);
    } else {
      setMobileStatus("");
      setMobileMsg("");
    }
  }, [formik.values.mobile_no]);

  // 5) Dynamic email check
  useEffect(() => {
    const email = formik.values.email_id;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (email && emailRegex.test(email)) {
      const timer = setTimeout(() => {
        CheckEmail(email)
          .then((res) => {
            if (res.data.exists) {
              setEmailStatus("exists");
              setEmailMsg("This email is already registered.");
            } else {
              setEmailStatus("available");
              setEmailMsg("Email available!");
            }
          })
          .catch(() => {
            setEmailStatus("");
            setEmailMsg("Error checking email.");
          });
      }, 400);
      return () => clearTimeout(timer);
    } else {
      setEmailStatus("");
      setEmailMsg("");
    }
  }, [formik.values.email_id]);

  // 6) Fetch “Terms and Conditions” when modal opens
  useEffect(() => {
    if (showTermsModal) {
      dispatch(fetchContent("terms_and_conditions"));
    }
  }, [showTermsModal, dispatch]);

  return (
    <div className="flex flex-col lg:flex-row h-[88vh]">
      {/* Left banner */}
      <div className="w-100 lg:w-1/2 flex justify-center">
        <img
          loading="lazy"
          src={loginBanner}
          alt="banner"
          className="h-[88vh] lg:w-[50vw]"
        />
      </div>

      {/* Right‐side form */}
      <div className="w-100 lg:w-1/2 px-[5vh] md:px-[10vh] lg:px-[15vh] lg:h-[88vh] lg:overflow-y-auto">
        <div className="w-100 lg:mx-5 pb-3 mt-4">
          <h6 className="fw-bold text-xl md:text-2xl lg:text-4xl">Register</h6>
          <form onSubmit={formik.handleSubmit} className="mt-4">
            {[
              { label: "Full Name", name: "vendor_name", type: "text" },
              { label: "Mobile Number", name: "mobile_no", type: "text" },
              { label: "Email ID", name: "email_id", type: "email" },
              { label: "Shop Name", name: "vendor_shop_name", type: "text" },
              { label: "GST Number", name: "gst_number", type: "text" },
              // { label: 'Address', name: 'address', type: 'textarea' },
              { label: "Pin Code", name: "pin_code", type: "text" },
            ].map(({ label, name, type }) => (
              <div className="mt-3" key={name}>
                <label className="text-lg lg:text-xl">
                  {label}{" "}
                  {[
                    "vendor_name",
                    "mobile_no",
                    "email_id",
                    "vendor_shop_name",
                    "address",
                    "pin_code",
                  ].includes(name) && <span className="text-red-600">*</span>}
                </label>

                {type === "textarea" ? (
                  <textarea
                    className="form-control mt-1 h-40"
                    name={name}
                    value={formik.values[name]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder={`Enter ${label}`}
                  />
                ) : (
                  <input
                    type={type}
                    className="form-control mt-1"
                    name={name}
                    value={formik.values[name]}
                    onChange={(e) => {
                      if (name === "mobile_no") {
                        const digits = e.target.value.replace(/\D/g, "");
                        formik.setFieldValue("mobile_no", digits);
                      } else {
                        formik.handleChange(e);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    placeholder={`Enter ${label}`}
                    maxLength={name === "mobile_no" ? 10 : undefined}
                  />
                )}

                {formik.touched[name] && formik.errors[name] && (
                  <p className="text-danger text-sm px-2">
                    {formik.errors[name]}
                  </p>
                )}

                {name === "mobile_no" &&
                  formik.values.mobile_no.length === 10 &&
                  mobileMsg && (
                    <div
                      className={
                        mobileStatus === "exists"
                          ? "text-red-600 text-sm"
                          : "text-green-600 text-sm"
                      }
                    >
                      {mobileMsg}
                    </div>
                  )}

                {name === "email_id" && emailMsg && formik.values.email_id && (
                  <div
                    className={
                      emailStatus === "exists"
                        ? "text-red-600 text-sm"
                        : "text-green-600 text-sm"
                    }
                  >
                    {emailMsg}
                  </div>
                )}
              </div>
            ))}

            {/* Terms & Conditions Checkbox */}
            <div className="mt-3 flex items-center gap-2">
              <input
                type="checkbox"
                id="terms"
                checked={agreed}
                onChange={() => setAgreed((prev) => !prev)}
              />
              <label htmlFor="terms" className="text-sm">
                I agree to the{" "}
                <button
                  type="button"
                  className="text-blue-600 underline"
                  onClick={() => setShowTermsModal(true)}
                >
                  Terms and Conditions
                </button>
              </label>
            </div>

            {/* Continue Button */}
            <div className="mt-4">
              <button
                type="submit"
                disabled={!agreed || loading}
                className="btn theme-outline-btn w-100"
              >
                {loading ? (
                  <div className="flex justify-center items-center gap-2">
                    <span className="animate-spin rounded-full h-5 w-5 border-t-2 border-blue-500"></span>{" "}
                    Submitting…
                  </div>
                ) : (
                  "Continue"
                )}
              </button>
            </div>
            <Link
              to="/login"
              className="text-blue-600 underline mt-4 flex items-center gap-1"
            >
              Already have an account? Login!
            </Link>
          </form>

          {/* Terms & Conditions Modal */}
          <Modal
            isOpen={showTermsModal}
            onClose={() => setShowTermsModal(false)}
            title="Terms and Conditions"
          >
            {tLoading && <p>Loading...</p>}
            {tError && (
              <p className="text-red-600">
                Failed to load Terms and Conditions.
              </p>
            )}
            {!tLoading && !tError && tData?.description && (
              // <div className="whitespace-pre-line">{tData.description}</div>
              <div className="whitespace-pre-line" dangerouslySetInnerHTML={{__html: tData.description}} />
            )}
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default VendorRegistration;
